import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './utils/experienceStore';
import { updateProcessorNode } from './utils/state/processingFlowSlice';
import URLInputWithLibrary from './URLInputWithLibrary';
import { Settings, X, FileText, ToggleLeft, ToggleRight } from 'lucide-react';

interface ProcessorPropertiesPanelProps {
    width?: number;
    onLaunchAssetPicker?: (callback: (value: string) => void) => void;
    onToggleCollapse?: () => void;
}

export default function ProcessorPropertiesPanel({
    width = 320,
    onLaunchAssetPicker,
    onToggleCollapse,
}: ProcessorPropertiesPanelProps) {
    const dispatch = useDispatch();

    const { nodes, selectedNodeId } = useSelector(
        (state: RootState) => state.flows.present,
    );
    const selectedNode = nodes.find((node) => node.id === selectedNodeId);

    // Determine if panel should be collapsed based on width
    const isCollapsed = width <= 60;

    const handleArgumentChange = (key: string, value: string | number) => {
        if (!selectedNode) return;

        // Cast all values to strings as required by the schema
        const stringValue = String(value);

        dispatch(
            updateProcessorNode({
                nodeId: selectedNode.id,
                updates: {
                    arguments: {
                        ...selectedNode.arguments,
                        [key]: stringValue,
                    },
                },
            }),
        );
    };

    const renderArgumentInput = (
        key: string,
        value: string | number,
        argumentType?: string,
    ) => {
        const inputId = `arg-${key}`;

        // Handle different argument types
        switch (argumentType) {
            case 'fileurl':
                return (
                    <URLInputWithLibrary
                        value={String(value || '')}
                        onChange={(newValue) =>
                            handleArgumentChange(key, newValue)
                        }
                        placeholder="Enter file URL or path"
                        label={key.charAt(0).toUpperCase() + key.slice(1)}
                    />
                );

            case 'boolean':
                return (
                    <button
                        onClick={() =>
                            handleArgumentChange(
                                key,
                                value === 'true' ? 'false' : 'true',
                            )
                        }
                        className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-colors ${
                            value === 'true'
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {value === 'true' ? (
                            <ToggleRight className="w-5 h-5" />
                        ) : (
                            <ToggleLeft className="w-5 h-5" />
                        )}
                        <span>{value === 'true' ? 'Enabled' : 'Disabled'}</span>
                    </button>
                );

            case 'number':
                return (
                    <input
                        id={inputId}
                        type="number"
                        value={value}
                        onChange={(e) =>
                            handleArgumentChange(
                                key,
                                parseFloat(e.target.value) || 0,
                            )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                );

            case 'select':
                // This would need to be enhanced with actual options from the processor definition
                return (
                    <select
                        id={inputId}
                        value={value}
                        onChange={(e) =>
                            handleArgumentChange(key, e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value={value}>{value}</option>
                    </select>
                );

            default:
                return (
                    <input
                        id={inputId}
                        type="text"
                        value={value}
                        onChange={(e) =>
                            handleArgumentChange(key, e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={`Enter ${key}`}
                    />
                );
        }
    };

    if (isCollapsed) {
        return (
            <div
                className="bg-white border-l border-gray-300 shadow-lg flex items-center justify-center h-full"
                style={{ width: 60 }}
            >
                <button
                    onClick={onToggleCollapse}
                    className="bg-white border border-gray-300 rounded-lg p-3 shadow-lg hover:shadow-xl transition-shadow"
                    title="Open Properties Panel"
                >
                    <Settings className="w-5 h-5 text-gray-600" />
                </button>
            </div>
        );
    }

    return (
        <div
            className="bg-white border-l border-gray-300 shadow-lg flex flex-col h-full"
            style={{ width }}
        >
            {/* Header */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Settings className="w-5 h-5 mr-2" />
                        Properties
                    </h3>
                    <button
                        onClick={onToggleCollapse}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        title="Collapse panel"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
                {!selectedNode ? (
                    <div className="p-6 text-center text-gray-500">
                        <Settings className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">
                            Select a processor to edit its properties
                        </p>
                    </div>
                ) : (
                    <div className="p-4 space-y-6">
                        {/* Processor Info */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-2">
                                {selectedNode.processor.title ||
                                    selectedNode.processor.name}
                            </h4>
                            {selectedNode.processor.description && (
                                <p className="text-sm text-gray-600 mb-3">
                                    {selectedNode.processor.description}
                                </p>
                            )}
                            <div className="flex items-center justify-between text-xs text-gray-500">
                                <span>
                                    Files: {selectedNode.processor.fileCount}
                                </span>
                                <span>
                                    Type:{' '}
                                    {selectedNode.processor.inputType || 'any'}
                                </span>
                            </div>
                        </div>

                        {/* Arguments */}
                        <div className="space-y-4">
                            <h5 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">
                                Arguments
                            </h5>

                            {Object.entries(selectedNode.arguments)
                                .filter(
                                    ([key]) => key !== 'in' && key !== 'out',
                                )
                                .map(([key, value]) => {
                                    const argumentType =
                                        selectedNode.processor.argumentTypes?.[
                                            key
                                        ];

                                    return (
                                        <div key={key} className="space-y-2">
                                            <label
                                                htmlFor={`arg-${key}`}
                                                className="block text-sm font-medium text-gray-700 capitalize"
                                            >
                                                {key
                                                    .replace(/([A-Z])/g, ' $1')
                                                    .replace(/^./, (str) =>
                                                        str.toUpperCase(),
                                                    )}
                                                {argumentType && (
                                                    <span className="ml-2 text-xs text-gray-500 font-normal">
                                                        ({argumentType})
                                                    </span>
                                                )}
                                            </label>
                                            {renderArgumentInput(
                                                key,
                                                value,
                                                argumentType,
                                            )}
                                        </div>
                                    );
                                })}

                            {Object.keys(selectedNode.arguments).filter(
                                (key) => key !== 'in' && key !== 'out',
                            ).length === 0 && (
                                <div className="text-center text-gray-500 py-4">
                                    <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                    <p className="text-sm">
                                        No configurable arguments
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Environment Info */}
                        {selectedNode.processor.environments && (
                            <div className="bg-blue-50 rounded-lg p-3">
                                <h6 className="text-sm font-medium text-blue-900 mb-1">
                                    Supported Environments
                                </h6>
                                <div className="flex flex-wrap gap-1">
                                    {selectedNode.processor.environments.map(
                                        (env) => (
                                            <span
                                                key={env}
                                                className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded"
                                            >
                                                {env}
                                            </span>
                                        ),
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
